import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import dayjs from 'dayjs';
import { hash, task } from 'ember-concurrency';
import type { Hotel, Hotelinfo, Ota } from 'frontend-utils/models';
import fnMedian from 'frontend-utils/utils/numeric/find-median';
import type { Rate } from 'lumen/models';
import type Period from 'lumen/models/period';
import type { PeriodParity, PeriodRate } from 'lumen/models/period';
import type ShortTermRentalRate from 'lumen/models/short-term-rental-rate';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type RatesService from 'lumen/services/rates';
import { SHORT_TERM_RENTAL_RATE_COLOR } from 'lumen/utils/constants/chart-color-constants';
import {
  MEALTYPES,
  type RateParam,
} from 'lumen/utils/constants/general-constants';
import getRateIcons from 'lumen/utils/rate-icons';
import {
  mapQueryParamsToAPIParams,
  requestRates,
} from 'lumen/utils/rates-request';
import { timeout } from 'lumen/utils/waiters';

type CompRateEntry = {
  allChannelsOtas: PeriodRate[];
  backgroundColor?: ReturnType<typeof htmlSafe>;
  extractDatetime: number;
  hasLosRestriction: boolean;
  hotel: Hotelinfo;
  isHoveredHotel: boolean;
  isOwnHotel: boolean;
  rate: ReturnType<typeof htmlSafe> | ReturnType<RatesService['formatRate']>;
  rateIcons: ReturnType<typeof getRateIcons>;
  roomName: string;
  sortByMessage: string | number;
  sortByValue: number;
  value: number;
};

export interface GeneralTooltipsTooltipRatesGenericSignature {
  Args: {
    competitorsForCurrentHotelAndOTA?: Hotelinfo[];
    currency?: string;
    fetchOtherLosRates?: boolean;
    isHovered?: boolean;
    isLoading?: boolean;
    isRatesEvolution?: boolean;
    median?: number;
    otaId: string | null | undefined;
    ownHotelId?: string | null;
    params: { los: number; otaId: string; rateParam: RateParam };
    passedHotel?: Hotel;
    period?: Period;
    rates?: Rate[];
    rentalRate?: ShortTermRentalRate;
    series?: { color: string; name: string }[];
  };
}

export default abstract class GeneralTooltipsTooltipRatesGeneric extends Component<GeneralTooltipsTooltipRatesGenericSignature> {
  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service declare dates: Services['dates'];
  @service('error-tracking') declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service('rates') declare ratesService: RatesService;
  @service declare store: Services['store'];

  @tracked competitorRatesWithDifferentLos: Record<
    string,
    Record<string, Rate>
  > = {};

  getCompetitorRatesWithDifferentLosTask = task(
    { restartable: true },
    async () => {
      const losKeys = this.losRestrictions;

      if (losKeys.length === 0) {
        return undefined;
      }

      try {
        // Prevent calling the API too often when sliding the cursor across the window
        await timeout();

        const startDate = this.args.period?.fromDate.format('YYYY-MM-DD') ?? '';
        const promiseHash: Record<string, Promise<Rate[]>> = {};
        let hashResult: Record<string, Rate[]> = {};
        let foundRates = false;

        losKeys.forEach((losRestriction) => {
          const rates = this.ratesService.getCachedLosRestrictionRates(
            startDate,
            losRestriction,
          );
          if (rates.length) {
            foundRates = true;
          }
          hashResult[losRestriction] = rates as Rate[];
        });

        if (!foundRates) {
          losKeys.forEach((losKey) => {
            const queryParams = { ...this.args.params };
            queryParams.los = losKey;

            const apiParams = mapQueryParamsToAPIParams(
              queryParams,
              startDate,
              startDate,
            );
            const ratesQuery = requestRates(
              'rate',
              this.hotelAndMarketService.hotel.id!,
              apiParams,
              this.store,
            );

            promiseHash[losKey] = ratesQuery;
          });

          // Pauses the task until the yield is resolved
          hashResult = await hash(promiseHash);
        }

        // Transformation (array to hash) so rate can be fetched directly by hotelinfo id in tooltip-rates-generic.ts
        // {los}->[rateArray]->rateObj
        // {los}->{rateHotelInfoIdHash}->rateObj
        const transformedHashResult: Record<string, Record<string, Rate>> = {};

        Object.keys(hashResult).forEach((losKey) => {
          const rates = hashResult[losKey];
          if (!rates) {
            return;
          }

          const losRates: Record<string, Rate> = {};
          transformedHashResult[losKey] = losRates;

          this.ratesService.setCachedLosRestrictionRates([...rates]);

          rates.forEach((rate) => {
            const hotelInfoId = rate.hotelinfo.id ?? '';
            losRates[hotelInfoId] = rate;
          });
        });

        this.competitorRatesWithDifferentLos = transformedHashResult;
      } catch (error) {
        this.errorTracking.captureException(error);
        void this.alerting.showErrorAlert();
      }
    },
  );

  get competitorsAndOwnHotel(): Hotelinfo[] {
    const competitorsAndOwnHotel: Hotelinfo[] = [];

    if (this.currentHotel) {
      competitorsAndOwnHotel.push(this.currentHotel.hotelinfo);
    }

    if (this.args.competitorsForCurrentHotelAndOTA) {
      competitorsAndOwnHotel.push(
        ...this.args.competitorsForCurrentHotelAndOTA,
      );
    }

    return competitorsAndOwnHotel;
  }

  get compRatesArray(): CompRateEntry[] {
    const arrCompetitorRates: CompRateEntry[] = [];

    this.competitorsAndOwnHotel.forEach((hotel) => {
      if (!hotel || !hotel.id) {
        return;
      }

      const rate = this.getRate(hotel);
      if (!rate) {
        return;
      }

      let allChannelsOtas: PeriodRate[] = [];
      let roomName = '';

      let otherLosRateValue: Rate | null = null;
      const hasLosRestriction = rate.message.indexOf('rates.restriction') === 0;
      // Look up the corresponding competitor rate value in the transformedHashResult response by hotelinfo id
      if (hasLosRestriction) {
        const los = rate.message.split('.').at(-1);
        const transformedHashResult = this.competitorRatesWithDifferentLos;

        if (los && transformedHashResult?.[los]) {
          // rate.hotelinfo is a numeric id in PeriodRate
          otherLosRateValue =
            transformedHashResult[los][String(rate.hotelinfo)] ?? null;
        }
      }

      const rateIcons = otherLosRateValue
        ? this.getRateIcons(otherLosRateValue)
        : this.getRateIcons(rate);

      if (this.args.otaId === 'allchannels') {
        const ratesObjHotel = this.rates?.[hotel.id];

        if (!ratesObjHotel) {
          return [];
        }

        allChannelsOtas = ratesObjHotel
          .filter((hotelRate) => hotelRate.position_to_baserate === 0)
          .sort((a, b) => a.ota.localeCompare(b.ota));
      } else if (!otherLosRateValue) {
        roomName = rate.room_name;
      } else {
        roomName = otherLosRateValue.roomName;
      }

      // The competitor prices in the rates tooltip are sorted in descending order by default
      let formattedRate = this.ratesService.formatRate(
        rate,
        this.currentHotel,
        true,
        false,
      );

      if (this.args.currency) {
        // Pass null as the hotel so the rates service uses the rate currency
        formattedRate = this.ratesService.formatRate(rate, null, true, false);
      }

      const competitor: CompRateEntry = {
        hotel,
        isOwnHotel: hotel.id === this.currentHotel?.hotelinfo?.id,
        isHoveredHotel: false,
        sortByValue:
          rate.value === 0 ? Number.MAX_SAFE_INTEGER : -1 * rate.value,
        sortByMessage: rate.message || Number.MAX_SAFE_INTEGER,
        hasLosRestriction,
        rate: otherLosRateValue
          ? htmlSafe(
              `${formattedRate} (${this.hotelAndMarketService.formatRate(otherLosRateValue)})`,
            )
          : formattedRate,
        value: rate.value,
        rateIcons,
        allChannelsOtas,
        roomName,
        // TODO: does this need to be a tolocalunixtime?
        extractDatetime: dayjs
          .utc(this.rates?.[hotel.id]?.[0]?.extract_datetime)
          .unix(),
      };

      if (this.args.series) {
        const series = this.args.series.find((x) => x && x.name === hotel.name);
        competitor.backgroundColor = series
          ? htmlSafe(`background-color: ${series.color}`)
          : htmlSafe('');
      }

      arrCompetitorRates.push(competitor);
    });

    return arrCompetitorRates.sort((a, b) => {
      if (a.sortByValue !== b.sortByValue) {
        return a.sortByValue - b.sortByValue;
      }
      if (a.sortByMessage !== b.sortByMessage) {
        return a.sortByMessage < b.sortByMessage ? -1 : 1;
      }
      return (a.hotel.name ?? '').localeCompare(b.hotel.name ?? '');
    });
  }

  get currentDate(): string | null {
    if (this.args.period?.fromDate) {
      return this.args.isRatesEvolution
        ? `${this.intl.t('rates.leadtime')} ${Math.abs(
            this.args.period?.leadtime,
          )} (${this.args.period?.extractDate.format(
            this.dates.partialDateFormat.WEEKDAY_AND_DATE_LONG,
          )})`
        : this.args.period?.fromDate.format(
            this.dates.partialDateFormat.WEEKDAY_AND_DATE_LONG,
          );
    }

    return null;
  }

  get currentHotel(): Hotel | undefined {
    const { passedHotel } = this.args;

    return passedHotel ?? this.hotelAndMarketService.hotel;
  }

  get formattedMedian(): string | number | null | undefined {
    const { median } = this.args;
    if (median === undefined || median <= 0) {
      return median;
    }
    return this.hotelAndMarketService.formatRate(median, true, false);
  }

  get isFutureDate(): boolean {
    return (
      !!this.args.period?.day?.date &&
      !this.args.period?.day?.date.isBefore(
        this.hotelAndMarketService.getLocalTime(),
        'day',
      )
    );
  }

  /**
   * returns all los that needs to be requested
   */
  get losRestrictions(): number[] {
    const losKeysToFetch = new Set<number>();
    const rates = this.args.period?.rates;

    this.competitorsAndOwnHotel.forEach((hotel) => {
      const hotelRates = rates?.[hotel.id!];
      if (hotelRates?.length) {
        const rate = hotelRates[0];
        if (
          rate.message.indexOf('rates.restriction') === 0 &&
          rate.ota === 'bookingdotcom'
        ) {
          // Get los numeric value from rate message
          const losInRateMessage = rate.message.match(/(\d+)/);

          const los = Number(losInRateMessage![0]);

          losKeysToFetch.add(los);
        }
      }
    });

    // Set to array
    return [...losKeysToFetch];
  }

  abstract get rates():
    | Record<string, [PeriodRate, ...PeriodRate[]]>
    | undefined;

  get shortTermRentalRate(): {
    backgroundColor: ReturnType<typeof htmlSafe>;
    name: string;
    numberOfProperties: number;
    rate: string | null | undefined;
    showIdentifierBullet: boolean;
  } | null {
    if (
      this.access.hasShortTermRentalAccess &&
      !this.access.hasShortTermRentalOnlyStrTab &&
      this.args.period
    ) {
      const rate =
        this.args.rentalRate ?? [...this.args.period.day.rentalRates][0];

      return {
        name: this.intl.t('short_term_rental.rate'),
        rate: rate?.formattedValue,
        showIdentifierBullet: this.args.series !== undefined,
        backgroundColor: htmlSafe(
          `background-color: ${SHORT_TERM_RENTAL_RATE_COLOR}`,
        ),
        numberOfProperties: this.ratesService.relevantRentalProperties,
      };
    }

    return null;
  }

  get updateTimeDate(): string {
    const ownHotelExtractDatetime = this.compRatesArray
      .filter((x) => x.isOwnHotel)
      .map((x) => x.extractDatetime)[0];

    if (ownHotelExtractDatetime !== undefined) {
      const extractDatetimeOwnHotel = dayjs.unix(ownHotelExtractDatetime);
      if (extractDatetimeOwnHotel > dayjs.utc().subtract(1, 'hour')) {
        return extractDatetimeOwnHotel.fromNow();
      }
    }

    let updateTimeDate = '';
    const compExtractDateTimeUnixArray = this.compRatesArray
      .filter((x) => x.extractDatetime !== 0)
      .map((x) => x.extractDatetime);

    const median = fnMedian(compExtractDateTimeUnixArray);
    if (
      compExtractDateTimeUnixArray.length &&
      median !== undefined &&
      median > 0
    ) {
      updateTimeDate = dayjs.unix(median).fromNow();
    }

    return updateTimeDate;
  }

  /**
   * Provides the rates map. Must be overridden by subclasses.
   */

  constructor(
    owner: Owner,
    args: GeneralTooltipsTooltipRatesGenericSignature['Args'],
  ) {
    super(owner, args);

    this.initializeCompetitorRatesWithDifferentLos();
  }

  getRate(hotel: Hotelinfo): PeriodRate | null {
    const { rates } = this;
    const hotelId = hotel.id ?? '';
    if (rates && rates[hotelId] && rates[hotelId][0]) {
      return rates[hotelId][0];
    }

    if (
      this.errorTracking &&
      hotel &&
      hotel.id === this.currentHotel?.hotelinfo?.id
    ) {
      this.errorTracking.addBreadcrumb({
        message: `Rates missing in the tooltip for ${hotel.id}`,
        data: { rates },
      });
    }

    return null;
  }

  getRateIcons(rate: PeriodRate | Rate): ReturnType<typeof getRateIcons> {
    const ota = this.store.peekRecord<Ota>('ota', this.args.otaId ?? '');

    return getRateIcons(
      rate as PeriodRate | PeriodParity,
      this.args.params.rateParam,
      ota,
      MEALTYPES.ANY,
    );
  }

  initializeCompetitorRatesWithDifferentLos(): void {
    if (
      this.args.fetchOtherLosRates &&
      this.args.params?.otaId !== 'allchannels'
    ) {
      // It's no necessary to await getCompetitorRatesWithDifferentLosTask because
      // when the task resolves it writes to @tracked competitorRatesWithDifferentLos,
      // which triggers a re-render automatically.
      void this.getCompetitorRatesWithDifferentLosTask.perform();
    }
  }
}
